"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/50 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent text-foreground/80 hover:text-secondary hover:bg-transparent font-medium transition-colors px-3")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileHeaderNavigation({
  isOpen,
  onClick
}: {
  isOpen: boolean;
  onClick: () => void;
}) {
  return <div className="flex items-center gap-2 md:hidden">
      <Button variant="secondary" size="icon" onClick={onClick} aria-label={isOpen ? "Close menu" : "Open menu"} className={cn("h-10 w-10 shadow-md transition-all duration-300 active:scale-95", isOpen && "bg-primary text-primary-foreground hover:bg-primary/90")}>
        {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </Button>
    </div>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-8 w-full bg-muted/50 animate-pulse rounded" />)}
      </div>;
  }
  const allowedLabels = ["Owners", "Tenants", "Available Rentals", "Contact"];
  const filteredMenuItems = menuItems.filter(item => allowedLabels.includes(item.label));
  return <nav className="flex flex-col p-6 space-y-1">
      {filteredMenuItems.map((item, i) => <Button key={item.id} data-index={i} asChild variant="ghost" className="w-full h-16 justify-between px-4 text-xl font-bold border-b border-border/40 text-foreground hover:text-secondary hover:bg-muted/10 transition-all group rounded-none">
          <Link to={item.href || "#"} onClick={onClose}>
            {item.label}
            <ChevronRight className="size-6 text-muted-foreground group-hover:text-secondary transition-transform group-hover:translate-x-1" />
          </Link>
        </Button>)}
      <div className="flex justify-center pt-8">
        <img src="https://noted-bug.10web.cloud/wp-content/uploads/2026/03/image_wro2kboy.png" alt="Realsource Property Management" className="h-7 object-contain !w-[100px]" />
      </div>
      <div className="pt-8 flex flex-col gap-4">
        <Button asChild className="w-full h-12 text-base font-bold bg-secondary hover:bg-secondary/90 text-white shadow-lg shadow-secondary/20">
          <Link to="https://noted-bug.10web.cloud/RentalAnalysisPage" onClick={onClose}>
            Request Rental Analysis
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return;
}